import React, { createContext, useContext, useState } from 'react'
import { useLocalStorage } from '../hooks/useLocalStorage'
// TODO: 04/21/24 build a retreival of onHandQty maintained by aws
// import useAxios from '../hooks/useAxios.js'
// import { config } from '../utilities/constants'
import { products } from '../data'
import {
    trackAddToCart,
    trackRemoveFromCart,
} from '../utilities/trackingEvents'
import detectInitialCurrencyIsCAD from '../utilities/detectInitialCurrency'

const ShoppingCartContext = createContext({})

export function useShoppingCart() {
    return useContext(ShoppingCartContext)
}

export function ShoppingCartProvider({ children }) {
    const [showCart, setShowCart] = useState(false)

    const [cart, setCart] = useLocalStorage('shopping-cart', [])
    // const [dev_mode, _] = useLocalStorage('dev_mode', false)

    // Initial currency: USD by default. For first-time visitors with NO
    // existing localStorage choice, only flip to CAD when both browser
    // language AND IANA timezone point to Canada (see detectInitialCurrency.js
    // for the rationale). Once the user makes any choice, useLocalStorage
    // persists it and this initial value is never used again.
    const [isCAD, setIsCADCurrency] = useLocalStorage(
        'isCAD',
        detectInitialCurrencyIsCAD(),
    )

    const data = products[isCAD ? 1 : 0] // grabbing contents of ../data/index.js which exports the fence_inv list
    const currency = isCAD ? 'CAD' : 'USD'
    const cartQty = cart.reduce((quantity, item) => item.quantity + quantity, 0)

    // Helper: look up full product object by productIDNum
    function findProduct(productIDNum) {
        return data.find((p) => p.productIDNum === productIDNum)
    }

    function getItemQty(productIDNum) {
        return (
            cart.find((item) => item.productIDNum === productIDNum)?.quantity ||
            0
        )
    }
    function incrItemQty(productIDNum) {
        const product = findProduct(productIDNum)
        if (product) trackAddToCart(product, 1, currency)

        setCart((currItems) => {
            const existing = currItems.find(
                (item) => item.productIDNum === productIDNum,
            )
            if (existing == null) {
                return [...currItems, { productIDNum, quantity: 1 }]
            }
            return currItems.map((item) =>
                item.productIDNum === productIDNum
                    ? { ...item, quantity: item.quantity + 1 }
                    : item,
            )
        })
    }
    function decrItemQty(productIDNum) {
        // Defensive: if the item isn't in cart (e.g. a stale/runaway caller
        // is invoking this after the item was already removed), do nothing.
        const existing = cart.find((item) => item.productIDNum === productIDNum)
        if (existing == null) return

        const product = findProduct(productIDNum)
        if (product) trackRemoveFromCart(product, 1, currency)

        setCart((currItems) => {
            const current = currItems.find(
                (item) => item.productIDNum === productIDNum,
            )
            if (current == null) return currItems
            if (current.quantity <= 1) {
                return currItems.filter(
                    (item) => item.productIDNum !== productIDNum,
                )
            }
            return currItems.map((item) =>
                item.productIDNum === productIDNum
                    ? { ...item, quantity: item.quantity - 1 }
                    : item,
            )
        })
    }
    function removeFromCart(productIDNum) {
        const product = findProduct(productIDNum)
        const existing = cart.find((item) => item.productIDNum === productIDNum)
        if (existing == null) return

        if (product) trackRemoveFromCart(product, existing.quantity, currency)

        setCart((currItems) =>
            currItems.filter((item) => item.productIDNum !== productIDNum),
        )
    }
    function setItemQty(productIDNum, qty) {
        setCart((currItems) => {
            if (
                currItems.find((item) => item.productIDNum === productIDNum) ==
                null
            ) {
                return [...currItems, { productIDNum, quantity: qty }]
            } else {
                return currItems.map((item) => {
                    if (item.productIDNum === productIDNum) {
                        return { ...item, quantity: qty }
                    } else {
                        return item
                    }
                })
            }
        })
    }
    function emptyCart() {
        setCart([])
    }

    return (
        <ShoppingCartContext.Provider
            value={{
                getItemQty,
                setItemQty,
                incrItemQty,
                decrItemQty,
                removeFromCart,
                emptyCart,
                showCart,
                setShowCart,
                cart,
                cartQty,
                isCAD,
                setIsCADCurrency,
                data,
                // dev_mode,
            }}
        >
            {children}
        </ShoppingCartContext.Provider>
    )
}
